import React, { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { DropDownList } from '@ast/magma/components/dropdownlist';

import { FieldBehaviorMode } from '@app/queryTyping';

import {
  SingleChoiceFieldFragment as SingleChoiceField,
} from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import {
  FieldComponent,
} from '@app/common/configurable-wizards/types';
import {
  combineNames, getFieldErrorMessage,
} from '@app/common/configurable-wizards/utils';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { CFFormField } from '../../common/CFFormField';

export const ComboboxFieldComponent: FieldComponent<SingleChoiceField> = ({ parent, field }) => {
  const combinedName = combineNames(parent, field.id);
  const commonValidationMessages = useCommonValidationMessages();
  const {
    formState: { errors },
    control,
  } = useFormContext();
  const error = getFieldErrorMessage(combinedName, errors, commonValidationMessages);

  const getItemLabel = useCallback((value: string) => {
    const label = field.choiceList?.find((item) => item?.id === value)?.text;
    return label || '';
  }, [field]);

  const defaultValue = useMemo(
    () => (field.choiceList?.find((item) => item?.id === field.selectedValue) ? field.selectedValue : ''),
    [field],
  );

  return (
    <Controller
      name={combinedName}
      data-stable-name="SingleChoiceComboboxController"
      defaultValue={defaultValue}
      rules={
        {
          required: field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED,
        }
      }
      render={(renderProps) => (
        <CFFormField
          error={error}
        >
          <DropDownList
            data-stable-name={combineNames('DropDownList', field.id)}
            data={field.choiceList?.map((ch) => ch?.id) ?? []}
            defaultValue={renderProps.field.value}
            {...renderProps.field}
            value={renderProps.field.value}
            onChange={(item) => {
              renderProps.field.onChange(item);
            }}
            label={field.label ?? ''}
            getItemLabel={getItemLabel}
            readonly={field.fieldBehaviorMode === FieldBehaviorMode.READONLY}
            disabled={!field.isEnabled}
            aria-invalid={!!error}
          />
        </CFFormField>
      )}
      control={control}
    />
  );
};
